import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { Col, Flex, Row } from 'antd';
import { AxiosResponse, isCancel } from 'axios';
import { useDebounce } from 'react-use';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useRecentSearches } from '@/hooks/useRecentSearches';
import type { SearchResultsFooterReporter } from '@/layouts/02-Authenticated/layout/03-Content/routes/04-Search/SearchResultsFooterState';
import { SEARCH_HORIZONTAL_GUTTER } from '@/layouts/02-Authenticated/layout/03-Content/routes/04-Search/searchTabs';
import { useSearchResultsFooterReporter } from '@/layouts/02-Authenticated/layout/03-Content/routes/04-Search/useSearchResultsFooterReporter';
import { useTagSubjects, useWatchlistSubjects, type SearchMatchMode, type SearchMode } from '@/queries';
import {
    AnimatedSearchSiderPanel,
    buildSearchAdvancedFiltersJsonLogic,
    getScopedSearchAdvancedFilters,
    getScopedSearchFilterOperators,
    getSearchFilterJoin,
    isJsonLogicRuleEmpty,
    SearchSiderToggleRow,
    unwrapSearchFilters,
} from '@/shared';
import { useUserPreferences } from '@/stores/database/entities/00-RBAC/99-UserPreferences';
import { SubjectReference, WorkspaceSubjectType } from '@/types';

import { resolveSearchPreferences, SearchPreferences } from '../../../../../preferences';
import RecentSearchHistory from '../../../components/RecentSearchHistory';
import SearchEmptyState from '../../../components/SearchEmptyState';
import SearchResultsSkeleton from '../../../components/SearchResultsSkeleton';
import {
    getCompanyClientFilters,
    getCompanyResultColumnFilter,
    handleReturnCompanyResultsBasedOnFilters,
    hasCompanyScreenerCriteria,
} from './helpers';
import Sider from './layout/01-Sider';
import CompanyResults from './layout/03-Content/views/01-Results';
import Metadata from './meta';

interface CompaniesProps {
    filters: any;
    handleChange: any;
    leftSiderCollapsed: boolean;
    matchMode: SearchMatchMode;
    onToggleLeftSider: () => void;
    onToggleRightSider: () => void;
    onResultsFooterChange: SearchResultsFooterReporter;
    query?: string;
    rightSiderCollapsed: boolean;
    searchMode: SearchMode;
}

interface PaginatedCompanySearchResponse {
    data: any[];
    pagination: {
        hasNextPage: boolean;
        page: number;
        pageSize: number;
        total: number;
        totalPages: number;
    };
}

const Companies: FC<CompaniesProps> = ({
    query,
    matchMode,
    searchMode,
    filters,
    handleChange,
    leftSiderCollapsed,
    onToggleLeftSider,
    onToggleRightSider,
    onResultsFooterChange,
    rightSiderCollapsed,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const { preferences } = useUserPreferences();
    const { hasFeature } = useEntitlements();
    const canUseAdvancedFilters = hasFeature('advancedFilters');
    const searchPreferences = resolveSearchPreferences(preferences?.search as SearchPreferences | undefined);
    const { groupRelatedListings, includePublicCompanies, pageSize, showRecentSearches } = searchPreferences.results;

    const [companyResults, setCompanyResults] = useState<any[] | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [debouncedQuery, setDebouncedQuery] = useState(query ?? '');
    const [searchLoading, setSearchLoading] = useState(false);
    const [serverTotal, setServerTotal] = useState(0);
    const requestSequence = useRef(0);
    const legacyFilters = useMemo(() => unwrapSearchFilters(filters), [filters]);
    const filterJoin = getSearchFilterJoin(filters);
    const filterOperators = useMemo(() => getScopedSearchFilterOperators(filters), [filters]);
    const advancedFilters = useMemo(() => getScopedSearchAdvancedFilters(filters, 'companies'), [filters]);
    const advancedFilterJsonLogic = useMemo(
        () => (canUseAdvancedFilters ? buildSearchAdvancedFiltersJsonLogic(advancedFilters) : undefined),
        [advancedFilters, canUseAdvancedFilters],
    );
    const hasAdvancedFilters = !isJsonLogicRuleEmpty(advancedFilterJsonLogic);
    const selectedTagIds = useMemo(
        () => (Array.isArray(legacyFilters.workspaceTags) ? legacyFilters.workspaceTags.map(String) : []),
        [legacyFilters.workspaceTags],
    );
    const selectedWatchlistIds = useMemo(
        () => (Array.isArray(legacyFilters.watchlists) ? legacyFilters.watchlists.map(String) : []),
        [legacyFilters.watchlists],
    );
    const hasPrimitiveFilters = selectedTagIds.length > 0 || selectedWatchlistIds.length > 0;
    const tagSubjectsQuery = useTagSubjects(selectedTagIds, WorkspaceSubjectType.SEARCH_COMPANY);
    const watchlistSubjectsQuery = useWatchlistSubjects(selectedWatchlistIds, WorkspaceSubjectType.SEARCH_COMPANY);
    const primitiveLoading = tagSubjectsQuery.isLoading || watchlistSubjectsQuery.isLoading;
    const primitiveSubjects = useMemo(() => {
        const subjects = [...(tagSubjectsQuery.data ?? []), ...(watchlistSubjectsQuery.data ?? [])];

        return Array.from(
            new Map(subjects.map((subject) => [`${subject.subjectType}:${subject.subjectId}`, subject])).values(),
        );
    }, [tagSubjectsQuery.data, watchlistSubjectsQuery.data]);
    const vendorFilters = useMemo(() => {
        return Object.fromEntries(
            Object.entries(legacyFilters).filter(([key]) => key !== 'watchlists' && key !== 'workspaceTags'),
        );
    }, [legacyFilters]);
    const hasScreenerFilters = useMemo(() => hasCompanyScreenerCriteria(vendorFilters), [vendorFilters]);
    const clientFilters = useMemo(() => getCompanyClientFilters(vendorFilters), [vendorFilters]);
    const hasActiveSearch = Boolean(query || hasPrimitiveFilters || hasScreenerFilters || hasAdvancedFilters);
    const serverSearchActive = Boolean(query || hasScreenerFilters || hasAdvancedFilters);
    const criteriaSignature = useMemo(
        () =>
            JSON.stringify({
                advancedFilterJsonLogic,
                filterJoin,
                filterOperators,
                fragment: debouncedQuery,
                matchMode,
                searchMode,
                vendorFilters,
            }),
        [advancedFilterJsonLogic, debouncedQuery, filterJoin, filterOperators, matchMode, searchMode, vendorFilters],
    );
    const previousCriteriaSignature = useRef(criteriaSignature);

    useDebounce(() => setDebouncedQuery(query ?? ''), 350, [query]);

    useEffect(() => {
        if ((query ?? '') !== debouncedQuery) {
            setSearchLoading(true);
            return;
        }

        if (!debouncedQuery && !hasScreenerFilters && !hasAdvancedFilters) {
            requestSequence.current += 1;
            setCompanyResults(null);
            setSearchLoading(false);
            setServerTotal(0);
            return;
        }

        const criteriaChanged = previousCriteriaSignature.current !== criteriaSignature;
        previousCriteriaSignature.current = criteriaSignature;

        if (criteriaChanged && currentPage !== 1) {
            setSearchLoading(true);
            setCurrentPage(1);
            return;
        }

        const requestedPage = currentPage;
        const controller = new AbortController();
        const requestId = requestSequence.current + 1;
        requestSequence.current = requestId;
        setSearchLoading(true);

        void (async () => {
            try {
                const res: AxiosResponse<PaginatedCompanySearchResponse> = await authorizedAxiosInstance.post(
                    '/api/search/vendors/fmp/companies',
                    {
                        advancedFilterJsonLogic,
                        fragment: debouncedQuery,
                        filterJoin,
                        filterOperators,
                        filters: vendorFilters,
                        includeConnectedCompanies: true,
                        matchMode,
                        page: requestedPage,
                        pageSize,
                        paginated: true,
                        searchMode,
                    },
                    { signal: controller.signal },
                );

                if (requestSequence.current !== requestId || res.status !== 200) {
                    return;
                }

                const results = (res.data?.data ?? []).filter((result) => result !== null);
                setCompanyResults(results);
                setServerTotal(res.data?.pagination?.total ?? results.length);
            } catch (error) {
                if (isCancel(error) || controller.signal.aborted || requestSequence.current !== requestId) {
                    return;
                }

                captureException(error);
                setCompanyResults([]);
                setServerTotal(0);
            } finally {
                if (requestSequence.current === requestId) {
                    setSearchLoading(false);
                }
            }
        })();

        return () => controller.abort();
    }, [
        criteriaSignature,
        currentPage,
        advancedFilterJsonLogic,
        debouncedQuery,
        filterJoin,
        filterOperators,
        hasScreenerFilters,
        hasAdvancedFilters,
        matchMode,
        pageSize,
        query,
        searchMode,
        vendorFilters,
    ]);

    const filteredResults = useMemo(() => {
        const primitiveResults = primitiveSubjects.map((subject: SubjectReference) => ({
            companyName: subject.subjectLabel ?? subject.subjectId,
            exchange: subject.metadata?.exchange,
            name: subject.subjectLabel ?? subject.subjectId,
            symbol: subject.subjectId,
            website: subject.metadata?.website,
            enrichment: {
                companyName: subject.subjectLabel ?? subject.subjectId,
                exchange: subject.metadata?.exchange,
                website: subject.metadata?.website,
            },
        }));
        const resultSource =
            query || hasScreenerFilters || hasAdvancedFilters ? companyResults || [] : primitiveResults;
        const allowedPrimitiveSubjects = new Set(
            primitiveSubjects.map((subject) => `${subject.subjectType}:${subject.subjectId}`),
        );
        const sourceFilteredResults = resultSource.filter((result) => {
            const resultSubjectKey = `${WorkspaceSubjectType.SEARCH_COMPANY}:${result?.symbol}`;

            if (!includePublicCompanies && result?.resultType !== 'nylasCompany') {
                return false;
            }

            return !hasPrimitiveFilters || allowedPrimitiveSubjects.has(resultSubjectKey);
        });

        return handleReturnCompanyResultsBasedOnFilters(
            clientFilters,
            sourceFilteredResults,
            filterJoin,
            filterOperators,
        );
    }, [
        clientFilters,
        companyResults,
        filterJoin,
        filterOperators,
        hasPrimitiveFilters,
        hasAdvancedFilters,
        hasScreenerFilters,
        includePublicCompanies,
        primitiveSubjects,
        query,
    ]);

    const isEmpty =
        hasActiveSearch &&
        !searchLoading &&
        !primitiveLoading &&
        (companyResults !== null || hasPrimitiveFilters) &&
        filteredResults.length === 0;
    const showResults = hasActiveSearch && !searchLoading && !primitiveLoading && filteredResults.length > 0;
    const totalResults = serverSearchActive ? serverTotal : filteredResults.length;
    const totalPages = Math.max(1, Math.ceil(totalResults / Math.max(1, pageSize)));
    const effectivePage = Math.min(currentPage, totalPages);
    const footerLoading = searchLoading || primitiveLoading || (serverSearchActive && companyResults === null);
    const handlePageChange = useCallback(
        (page: number) => {
            if (page === currentPage) {
                return;
            }

            setSearchLoading(true);
            setCurrentPage(page);
        },
        [currentPage],
    );
    const handleApplyResultColumnFilter = useCallback(
        (column: string, result: unknown) => {
            const columnFilter = getCompanyResultColumnFilter(column, result, legacyFilters);

            if (columnFilter) {
                handleChange(columnFilter.path, columnFilter.value);
            }
        },
        [handleChange, legacyFilters],
    );

    useSearchResultsFooterReporter({
        active: hasActiveSearch,
        loading: footerLoading,
        onPageChange: handlePageChange,
        page: effectivePage,
        pageSize,
        report: onResultsFooterChange,
        total: totalResults,
    });

    const recentHistory = useRecentSearches();
    const { addSearch } = recentHistory;

    useEffect(() => {
        if (query && !searchLoading && companyResults && companyResults.length > 0) {
            addSearch(query);
        }
    }, [addSearch, companyResults, query, searchLoading]);

    return (
        <>
            <Metadata />
            <div
                style={{
                    boxSizing: 'border-box',
                    height: '100%',
                    minHeight: 0,
                    overflow: 'hidden',
                    padding: `0 ${SEARCH_HORIZONTAL_GUTTER}`,
                }}
            >
                <Row align='top' gutter={[24, 0]} style={{ height: '100%', minHeight: 0 }} wrap={false}>
                    <AnimatedSearchSiderPanel
                        open={!leftSiderCollapsed}
                        padding='0.5rem 0 0'
                        siderLabel='Search filters'
                        style={{ height: '100%', minHeight: 0 }}
                        width='20.833333%'
                    >
                        <div style={{ height: '100%', minHeight: 0 }}>
                            <Sider filters={filters} handleChange={handleChange} />
                        </div>
                    </AnimatedSearchSiderPanel>
                    <Col
                        flex='auto'
                        style={{
                            boxSizing: 'border-box',
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            minHeight: 0,
                            minWidth: 0,
                            paddingLeft: 0,
                            paddingRight: 0,
                            paddingTop: '0.5rem',
                        }}
                    >
                        {showResults ? (
                            <CompanyResults
                                companyResults={filteredResults}
                                groupRelatedListings={groupRelatedListings}
                                leftSiderCollapsed={leftSiderCollapsed}
                                onApplyColumnFilter={handleApplyResultColumnFilter}
                                onToggleLeftSider={onToggleLeftSider}
                                onToggleRightSider={onToggleRightSider}
                                page={effectivePage}
                                resultsPerPage={pageSize}
                                rightSiderCollapsed={rightSiderCollapsed}
                                serverPaginated={serverSearchActive}
                            />
                        ) : (
                            <>
                                <SearchSiderToggleRow
                                    leftSiderCollapsed={leftSiderCollapsed}
                                    onToggleLeftSider={onToggleLeftSider}
                                    onToggleRightSider={onToggleRightSider}
                                    rightSiderCollapsed={rightSiderCollapsed}
                                />
                                <div
                                    style={{
                                        flex: '1 1 0%',
                                        minHeight: 0,
                                        overflowX: 'hidden',
                                        overflowY: 'auto',
                                    }}
                                >
                                    {/* Skeleton */}
                                    {(searchLoading || primitiveLoading) && (
                                        <SearchResultsSkeleton domain='companies' />
                                    )}
                                    {/* Empty Initial State */}
                                    {!hasActiveSearch && !searchLoading && !primitiveLoading && (
                                        <>
                                            {!showRecentSearches ? (
                                                <SearchEmptyState height='100%' mode='initial' scope='companies' />
                                            ) : (
                                                <RecentSearchHistory
                                                    groups={recentHistory.recentGroups}
                                                    onClear={recentHistory.clearRecentSearches}
                                                    queries={recentHistory.recentQueries}
                                                    resources={recentHistory.recentResources}
                                                    scope='companies'
                                                />
                                            )}
                                        </>
                                    )}
                                    {/* No Results */}
                                    {isEmpty && (
                                        <Flex
                                            vertical
                                            align='center'
                                            justify='center'
                                            style={{
                                                height: '100%',
                                                minHeight: '15rem',
                                                color: token.colorTextTertiary,
                                                textAlign: 'center',
                                            }}
                                        >
                                            <SearchEmptyState mode='no-results' scope='companies' />
                                        </Flex>
                                    )}
                                </div>
                            </>
                        )}
                    </Col>
                </Row>
            </div>
        </>
    );
};

export default Companies;
